import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface ReviewTask {
  id: string;
  user_id: string;
  bill_id?: string;
  queue_item_id?: string;
  customer_id?: string;
  task_type: 'validation_review' | 'data_correction' | 'mandatory_field_review' | 'anomaly_review' | 'ai_processing_failure';
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'escalated';
  priority: number;
  assigned_to?: string;
  validation_failures: any[];
  missing_mandatory_fields: string[];
  original_data: any;
  reviewed_data: any;
  reviewer_notes?: string;
  created_at: string;
  assigned_at?: string;
  completed_at?: string;
  due_date?: string;
  estimated_duration_minutes: number;
}

export interface CreateReviewTaskData {
  bill_id?: string;
  queue_item_id?: string;
  task_type: ReviewTask['task_type'];
  priority?: number;
  validation_failures?: any[];
  missing_mandatory_fields?: string[];
  original_data?: any;
  due_date?: string;
  estimated_duration_minutes?: number;
}

export interface UpdateReviewTaskData {
  status?: ReviewTask['status'];
  assigned_to?: string;
  reviewed_data?: any;
  reviewer_notes?: string;
  completed_at?: string;
}

export const useReviewTasks = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all review tasks
  const tasksQuery = useQuery({
    queryKey: ['review-tasks', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('review-task-manager', {
        body: {
          action: 'list',
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to fetch review tasks');
      
      return data.data as ReviewTask[];
    },
    enabled: !!user?.id,
  });

  // Create review task mutation
  const createTaskMutation = useMutation({
    mutationFn: async (taskData: CreateReviewTaskData) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('review-task-manager', {
        body: {
          action: 'create',
          data: taskData,
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to create review task');
      
      return data.data;
    },
    onSuccess: () => {
      toast({ title: "Review task created", description: "Review task has been created successfully" });
      queryClient.invalidateQueries({ queryKey: ['review-tasks'] });
    },
    onError: (error: any) => {
      toast({ title: "Failed to create review task", description: error.message, variant: "destructive" });
    },
  });

  // Update review task mutation
  const updateTaskMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateReviewTaskData }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data: result, error } = await supabase.functions.invoke('review-task-manager', {
        body: {
          action: 'update',
          id,
          data,
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!result.success) throw new Error(result.error || 'Failed to update review task');
      
      return result.data;
    },
    onSuccess: () => {
      toast({ title: "Review task updated", description: "Review task has been updated successfully" });
      queryClient.invalidateQueries({ queryKey: ['review-tasks'] });
    },
    onError: (error: any) => {
      toast({ title: "Failed to update review task", description: error.message, variant: "destructive" });
    },
  });

  // Complete review task mutation
  const completeTaskMutation = useMutation({
    mutationFn: async ({ id, reviewed_data, reviewer_notes }: { id: string; reviewed_data: any; reviewer_notes?: string }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('review-task-manager', {
        body: {
          action: 'complete',
          id,
          data: {
            reviewed_data,
            reviewer_notes,
            status: 'completed',
            completed_at: new Date().toISOString()
          },
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to complete review task');
      
      return data.data;
    },
    onSuccess: () => {
      toast({ title: "Review task completed", description: "Review task has been completed successfully" });
      queryClient.invalidateQueries({ queryKey: ['review-tasks'] });
    },
    onError: (error: any) => {
      toast({ title: "Failed to complete review task", description: error.message, variant: "destructive" });
    },
  });

  // Assign task to user
  const assignTaskMutation = useMutation({
    mutationFn: async ({ id, assigned_to }: { id: string; assigned_to: string }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('review-task-manager', {
        body: {
          action: 'assign',
          id,
          data: {
            assigned_to,
            status: 'assigned',
            assigned_at: new Date().toISOString()
          },
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to assign review task');
      
      return data.data;
    },
    onSuccess: () => {
      toast({ title: "Review task assigned", description: "Review task has been assigned successfully" });
      queryClient.invalidateQueries({ queryKey: ['review-tasks'] });
    },
    onError: (error: any) => {
      toast({ title: "Failed to assign review task", description: error.message, variant: "destructive" });
    },
  });

  // Create review task from failed AI processing
  const createTaskFromFailedProcessingMutation = useMutation({
    mutationFn: async ({ queue_item_id, extracted_data, processing_error }: {
      queue_item_id: string;
      extracted_data?: any;
      processing_error: string;
    }) => {
      if (!user?.id) throw new Error('User not authenticated');

      const { data, error } = await supabase.functions.invoke('review-task-manager', {
        body: {
          action: 'create_from_failed_processing',
          data: { queue_item_id, extracted_data, processing_error },
          user_id: user.id
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || 'Failed to create review task');
      
      return data.data;
    },
    onSuccess: () => {
      toast({ title: "Review Task Created", description: "A review task has been created for the failed processing" });
      queryClient.invalidateQueries({ queryKey: ['review-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['bill-queue'] });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  // Helper functions for filtering tasks
  const pendingTasks = tasksQuery.data?.filter(task => task.status === 'pending') || [];
  const assignedTasks = tasksQuery.data?.filter(task => task.status === 'assigned') || [];
  const inProgressTasks = tasksQuery.data?.filter(task => task.status === 'in_progress') || [];
  const completedTasks = tasksQuery.data?.filter(task => task.status === 'completed') || [];
  const escalatedTasks = tasksQuery.data?.filter(task => task.status === 'escalated') || [];

  // Get tasks by priority
  const highPriorityTasks = tasksQuery.data?.filter(task => task.priority >= 80) || [];
  const mediumPriorityTasks = tasksQuery.data?.filter(task => task.priority >= 50 && task.priority < 80) || [];
  const lowPriorityTasks = tasksQuery.data?.filter(task => task.priority < 50) || [];

  return {
    tasks: tasksQuery.data || [],
    pendingTasks,
    assignedTasks,
    inProgressTasks,
    completedTasks,
    escalatedTasks,
    highPriorityTasks,
    mediumPriorityTasks,
    lowPriorityTasks,
    isLoading: tasksQuery.isLoading,
    error: tasksQuery.error,
    createTask: (data: CreateReviewTaskData) => createTaskMutation.mutate(data),
    createTaskFromFailedProcessing: createTaskFromFailedProcessingMutation.mutate,
    updateTask: (id: string, data: UpdateReviewTaskData) => updateTaskMutation.mutate({ id, data }),
    completeTask: (id: string, reviewed_data: any, reviewer_notes?: string) => 
      completeTaskMutation.mutate({ id, reviewed_data, reviewer_notes }),
    assignTask: (id: string, assigned_to: string) => assignTaskMutation.mutate({ id, assigned_to }),
    isCreating: createTaskMutation.isPending,
    isUpdating: updateTaskMutation.isPending,
    isCompleting: completeTaskMutation.isPending,
    isAssigning: assignTaskMutation.isPending,
  };
};